import { Badge, Button, Icon, List, ListRow, Section, Skeleton, Text, useToast } from '@ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage } from '../../../lib/errors';
import styles from './harvest.module.css';

export interface CatalogSectionProps {
  title: string;
  /** Shows hidden entries too. */
  hidden: boolean;
  onHidden: (hidden: boolean) => void;
  newLabel: string;
  onNew?: (() => void) | undefined;
  loading: boolean;
  /** Shown instead of the rows when there are none. */
  empty?: string | undefined;
  children: ReactNode;
}

/** One catalog in Einstellungen → Kataloge: rows, "+ anlegen", show hidden (BIZ-003). */
export function CatalogSection({
  title,
  hidden,
  onHidden,
  newLabel,
  onNew,
  loading,
  empty,
  children,
}: CatalogSectionProps) {
  const { t } = useTranslation();
  return (
    <Section
      title={title}
      action={
        onNew && (
          <Button
            variant="plain"
            size="sm"
            iconStart={<Icon name="plus" size="sm" />}
            onClick={onNew}
          >
            {newLabel}
          </Button>
        )
      }
      footer={
        <Button variant="plain" size="sm" onClick={() => onHidden(!hidden)}>
          {hidden ? t('apiary.catalogs.hideHidden') : t('apiary.catalogs.showHidden')}
        </Button>
      }
    >
      {loading && <Skeleton lines={3} />}
      {empty && !loading ? (
        <Text tone="muted" className={styles.empty}>
          {empty}
        </Text>
      ) : (
        <List label={title}>{children}</List>
      )}
    </Section>
  );
}

export interface CatalogRowProps {
  name: string;
  /** Facts for the grey line, empty ones are left out. */
  facts?: (string | null | undefined)[];
  builtin?: boolean;
  archived: boolean;
  onEdit?: (() => void) | undefined;
  /** Hides or shows the entry again; without it there is no menu. */
  onToggle?: (() => void) | undefined;
}

/** A catalog entry with its `…` menu: edit, hide / show. */
export function CatalogRow({
  name,
  facts = [],
  builtin = false,
  archived,
  onEdit,
  onToggle,
}: CatalogRowProps) {
  const { t } = useTranslation();
  const rowMenu = useRowMenu();
  const description = [builtin ? t('apiary.catalogs.builtin') : null, ...facts]
    .filter(Boolean)
    .join(' · ');
  return (
    <ListRow
      title={name}
      description={description}
      trailing={archived && <Badge size="sm">{t('apiary.catalogs.hidden')}</Badge>}
      actionsLabel={rowMenu.label(name)}
      actions={
        onToggle
          ? [
              onEdit && rowMenu.edit(onEdit),
              {
                key: 'toggle',
                label: archived ? t('apiary.catalogs.show') : t('apiary.catalogs.hide'),
                onSelect: onToggle,
              },
            ]
          : []
      }
    />
  );
}

interface ArchiveMutation {
  mutate(
    variables: { id: string; archived: boolean },
    options?: { onSuccess?: () => void; onError?: (error: Error) => void },
  ): void;
}

/** Hiding is reversible: it happens at once with an undo (docs/ux-richtlinien.md §6.10). */
export function useHideToggle(archive: ArchiveMutation) {
  const { t } = useTranslation();
  const toast = useToast();
  return (entry: { id: string; name: string; archived: boolean }) => {
    const hide = !entry.archived;
    archive.mutate(
      { id: entry.id, archived: hide },
      {
        onSuccess: () =>
          toast.show(
            hide
              ? t('apiary.catalogs.hiddenToast', { name: entry.name })
              : t('apiary.catalogs.shownToast', { name: entry.name }),
            'success',
            hide
              ? {
                  label: t('common.undo'),
                  onClick: () => archive.mutate({ id: entry.id, archived: false }),
                }
              : undefined,
          ),
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );
  };
}
